import React, {
  useState,
  useCallback,
  useEffect,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  Mail,
  UserRound,
  AlertTriangle,
  ArrowRight,
  Crown,
  Check,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

import API from "../../services/api";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import logo from "../../assets/logo.png";
import bg from "../../assets/bg.png";

import "../../styles/register.css";

export default function Register() {
  const navigate = useNavigate();

  /* =========================================================
     FORMULARIO
  ========================================================= */

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  /* =========================================================
     ANIMACIÓN
  ========================================================= */

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 60);

    return () => clearTimeout(timer);
  }, []);

  /* =========================================================
     CAMBIO DE INPUT
  ========================================================= */

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError("");
  }, []);

  /* =========================================================
     VALIDACIÓN
  ========================================================= */

  const validateForm = useCallback(() => {
    const username = form.username.trim();
    const email = form.email.trim();
    const password = form.password;

    if (!username) {
      setError("Ingresa un nombre de usuario.");
      return false;
    }

    if (username.length < 3) {
      setError(
        "El nombre de usuario debe tener al menos 3 caracteres."
      );
      return false;
    }

    if (!email) {
      setError("Ingresa tu correo electrónico.");
      return false;
    }

    if (!email.includes("@")) {
      setError(
        "Ingresa un correo electrónico válido."
      );
      return false;
    }

    if (!password) {
      setError("Ingresa una contraseña.");
      return false;
    }

    if (password.length < 6) {
      setError(
        "La contraseña debe tener al menos 6 caracteres."
      );
      return false;
    }

    return true;
  }, [form]);

  /* =========================================================
     REGISTRO
  ========================================================= */

  const registerRequest = useCallback(
    async () => {
      if (loading) return;

      if (!validateForm()) return;

      setLoading(true);
      setError("");

      try {
        const response = await API.post(
          "auth/register/",
          {
            username: form.username.trim(),
            email: form.email.trim(),
            password: form.password,
          }
        );

        console.log(
          "REGISTER SUCCESS:",
          response.data
        );

        navigate("/login", {
          replace: true,
          state: {
            message:
              "Cuenta creada correctamente. Ahora inicia sesión.",
          },
        });
      } catch (err) {
        console.error(
          "REGISTER ERROR:",
          err.response?.data || err
        );

        const backendErrors =
          err.response?.data?.errors;

        const usernameError =
          backendErrors?.username?.[0];

        const emailError =
          backendErrors?.email?.[0];

        const passwordError =
          backendErrors?.password?.[0];

        const nonFieldError =
          backendErrors?.non_field_errors?.[0];

        setError(
          usernameError ||
            emailError ||
            passwordError ||
            nonFieldError ||
            err.response?.data?.message ||
            err.response?.data?.detail ||
            err.response?.data?.error ||
            "No fue posible crear la cuenta."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      validateForm,
      form,
      navigate,
    ]
  );

  /* =========================================================
     ENTER
  ========================================================= */

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        registerRequest();
      }
    },
    [registerRequest]
  );

  /* =========================================================
     LOGIN
  ========================================================= */

  const goToLogin = useCallback(() => {
    if (loading) return;

    navigate("/login");
  }, [loading, navigate]);

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className={`register-page ${
        mounted
          ? "register-page--visible"
          : ""
      }`}
      style={{
        backgroundImage: `url(${bg})`,
      }}
    >
      {/* =====================================================
          CAPAS DE FONDO
      ===================================================== */}

      <div className="register-page__overlay" />

      <div className="register-page__noise" />

      <div className="register-page__scanlines" />

      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <Navbar />

      {/* =====================================================
          CONTENIDO
      ===================================================== */}

      <main className="register-main">
        <section className="register-card">

          {/* =================================================
              DECORACIÓN SUPERIOR
          ================================================= */}

          <div className="register-card__top-line" />

          <div className="register-card__corner register-card__corner--tl" />
          <div className="register-card__corner register-card__corner--tr" />
          <div className="register-card__corner register-card__corner--bl" />
          <div className="register-card__corner register-card__corner--br" />

          {/* =================================================
              HEADER
          ================================================= */}

          <div className="register-card__header">

            <div className="register-logo-wrap">

              <div className="register-logo-crown">
                <Crown size={30} />
              </div>

              <img
                src={logo}
                alt="Debuts Crop"
                className="register-logo"
              />

            </div>

            <div className="register-heading">

              <div className="register-heading__eyebrow">
                <Sparkles size={14} />
                <span>DEBUTS CROP · JOIN THE CULT</span>
              </div>

              <h1>
                CREAR CUENTA
              </h1>

              <p>
                Forma parte de la comunidad Debuts Crop.
              </p>

            </div>

          </div>

          {/* =================================================
              FORMULARIO
          ================================================= */}

          <form
            className="register-form"
            onSubmit={(event) => {
              event.preventDefault();
              registerRequest();
            }}
          >

            {/* ===============================================
                USUARIO
            =============================================== */}

            <div
              className={`register-input ${
                form.username
                  ? "register-input--filled"
                  : ""
              }`}
            >
              <UserRound
                size={20}
                className="register-input__icon"
              />

              <input
                type="text"
                name="username"
                value={form.username}
                placeholder="Nombre de usuario"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="username"
                aria-label="Nombre de usuario"
                autoFocus
              />
            </div>

            {/* ===============================================
                EMAIL
            =============================================== */}

            <div
              className={`register-input ${
                form.email
                  ? "register-input--filled"
                  : ""
              }`}
            >
              <Mail
                size={20}
                className="register-input__icon"
              />

              <input
                type="email"
                name="email"
                value={form.email}
                placeholder="Correo electrónico"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="email"
                aria-label="Correo electrónico"
              />
            </div>

            {/* ===============================================
                CONTRASEÑA
            =============================================== */}

            <div
              className={`register-input ${
                form.password
                  ? "register-input--filled"
                  : ""
              }`}
            >
              <LockKeyhole
                size={20}
                className="register-input__icon"
              />

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                name="password"
                value={form.password}
                placeholder="Contraseña"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="new-password"
                aria-label="Contraseña"
              />

              <button
                type="button"
                className="register-input__toggle"
                onClick={() =>
                  setShowPassword(
                    (previous) => !previous
                  )
                }
                disabled={loading}
                aria-label={
                  showPassword
                    ? "Ocultar contraseña"
                    : "Mostrar contraseña"
                }
              >
                {showPassword ? (
                  <EyeOff size={19} />
                ) : (
                  <Eye size={19} />
                )}
              </button>
            </div>

            {/* ===============================================
                REQUISITO DE CONTRASEÑA
            =============================================== */}

            <div className="register-password-info">

              <div className="register-password-info__icon">
                <Check size={13} />
              </div>

              <span>
                Mínimo 6 caracteres para proteger tu cuenta.
              </span>

            </div>

            {/* ===============================================
                ERROR
            =============================================== */}

            {error && (
              <div className="register-error">

                <AlertTriangle size={17} />

                <span>
                  {error}
                </span>

              </div>
            )}

            {/* ===============================================
                BOTÓN
            =============================================== */}

            <button
              type="submit"
              className="register-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2
                    size={20}
                    className="register-spinner"
                  />

                  <span>
                    CREANDO CUENTA...
                  </span>
                </>
              ) : (
                <>
                  <span>
                    CREAR CUENTA
                  </span>

                  <ArrowRight size={21} />
                </>
              )}
            </button>

            {/* ===============================================
                SEGURIDAD
            =============================================== */}

            <div className="register-security">

              <ShieldCheck size={16} />

              <span>
                Tus datos se mantienen protegidos.
              </span>

            </div>

            {/* ===============================================
                DIVISOR
            =============================================== */}

            <div className="register-divider">
              <span />
              <small>DEBUTS CROP</small>
              <span />
            </div>

            {/* ===============================================
                LOGIN
            =============================================== */}

            <div className="register-login">

              <span>
                ¿Ya tienes una cuenta?
              </span>

              <button
                type="button"
                onClick={goToLogin}
                disabled={loading}
              >
                Iniciar sesión
              </button>

            </div>

          </form>

          {/* =================================================
              FOOTER DE LA CARD
          ================================================= */}

          <div className="register-card__bottom">

            <span>
              TU CULTIVO
            </span>

            <i>·</i>

            <span>
              TU ESTILO
            </span>

            <i>·</i>

            <span>
              TU ESPACIO
            </span>

          </div>

        </section>
      </main>

      {/* =====================================================
          FOOTER GENERAL
      ===================================================== */}

      <Footer />

    </div>
  );
}